import { shopifyGraphQLRequest } from './graphqlClient.js';

export class NewShopifyImpl {
  constructor() {
    this.localStorageCheckoutIdKey = 'occo-shopify-checkout-id-key';
    this.PRODUCT_OPTION_POOL_PREFIX = 'pool_prefix';
    this.PRODUCT_OPTION_UNLOCK_MEDIA = 'unlock_media';
    this.PRODUCT_OPTION_IS_VIP = 'is_vip';
    this.SHIPPING_ADDRESS = localStorage.getItem('shippingAddress');
  }

  getCheckout(isFromWebapp, callback, attempt = 5) {
    this.isFromWebapp = isFromWebapp;
    if (typeof callback === 'function') {
      this.getOrCreateCart()
        .then((cart) => callback(cart))
        .catch((err) => {
          console.error('Error getting cart in getCheckout:', err);
          if (attempt > 1) {
            setTimeout(() => this.getCheckout(callback, attempt - 1), 500);
          } else {
            callback(null);
          }
        });
    } else {
      return this.getOrCreateCart();
    }
  }

  createCheckout(callback) {
    if (typeof callback === 'function') {
      this.createAndStoreCart([])
        .then((cart) => callback(cart))
        .catch(() => callback(null));
    } else {
      return this.createAndStoreCart([]);
    }
  }

  addLineItems(cartId, lineItems) {
    const lines = (lineItems || []).map((item) => ({
      merchandiseId: item.variantId,
      quantity: item.quantity || 1,
      attributes: item.customAttributes || [],
    }));
    return this.cartLinesAdd(cartId, lines);
  }

  async updateLineItems(cartId, oldLineItems) {
    const lines = oldLineItems.map((item) => ({
      id: item.id,
      quantity: item.quantity,
      attributes: item.customAttributes || [],
    }));

    // 2) Build the GraphQL mutation
    const query = `
      mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
        cartLinesUpdate(cartId: $cartId, lines: $lines) {
          cart {
            id
            webUrl: checkoutUrl
            lines(first: 50) {
              edges {
                node {
                  id
                  quantity
                  attributes {
                    key
                    value
                  }
                  merchandise {
                    ... on ProductVariant {
                      id
                      title
                      image {
                        src: url
                        altText
                      }
                      product {
                        id
                        title
                        description
                        handle
                      }
                      priceV2 {
                        amount
                        currencyCode
                      }
                    }
                  }
                  discountAllocations {
                    discountApplication {
                      value {
                        ... on PricingPercentageValue {
                          percentage
                        }
                        ... on MoneyV2 {
                          amount
                        }
                      }
                    }
                    allocatedAmount: discountedAmount {
                      amount
                      currencyCode
                    }
                  }
                }
              }
            }
            cost {
              subtotalPrice: subtotalAmount {
                amount
                currencyCode
              }
              totalTax: totalTaxAmount {
                amount
                currencyCode
              }
              totalPrice: totalAmount {
                amount
                currencyCode
              }
            }
            discountAllocations {
              discountApplication {
                value {
                  ... on PricingPercentageValue {
                    percentage
                  }
                  ... on MoneyV2 {
                    amount
                  }
                }
              }
              allocatedAmount:discountedAmount {
                amount
                currencyCode
              }
            }
            discountCodes {
              code
              applicable
            }
          }
          userErrors {
            field
            message
          }
        }
      }
    `;

    // 3) Execute the GraphQL request
    const variables = { cartId, lines };
    const response = await shopifyGraphQLRequest(query, variables);

    // 4) Error handling / userErrors
    const updatePayload = response.cartLinesUpdate;
    if (!updatePayload || updatePayload.userErrors?.length) {
      const errors = updatePayload?.userErrors || [];
      console.error('cartLinesUpdate errors', errors);
      throw new Error('Failed to update cart lines');
    }

    // 5) Return the updated cart
    this._removeDraftOrderInfoFromLocalStorage();
    const flatCart = this._flattenCost(updatePayload.cart);
    flatCart.lineItems = this._flattenCartLines(flatCart);
    flatCart.discountApplications = this._fillDiscountApplications(flatCart);
    return flatCart;
  }

  getCollection(collectionId) {
    const collectionGid = `gid://shopify/Collection/${collectionId}`;
    return this.fetchCollectionById(collectionGid);
  }

  cleanCheckout() {
    return this.resetCart();
  }

  /**
   * Applies discount codes to an existing cart (supports callback or promise).
   *
   * @param {string} cartId - The ID of the cart to update.
   * @param {Array<string>} discountCodes - An array of discount code strings to apply.
   * @param {Function} [callback] - (Optional) A callback to handle the result or error.
   * @returns {Promise<Object>|void} - Returns a Promise if callback is not provided.
   */
  async applyDiscount(cartId, discountCode, callback = null) {
    const query = `
      mutation cartDiscountCodesUpdate($cartId: ID!, $discountCodes: [String!]!) {
        cartDiscountCodesUpdate(cartId: $cartId, discountCodes: $discountCodes) {
          cart {
            id
            webUrl: checkoutUrl
            discountCodes {
              code
              applicable
            }
            discountAllocations {
              discountApplication {
                value {
                  ... on PricingPercentageValue {
                    percentage
                  }
                  ... on MoneyV2 {
                    amount
                  }
                }
              }
              allocatedAmount:discountedAmount {
                amount
                currencyCode
              }
            }
            lines(first: 50) {
              edges {
                node {
                  id
                  quantity
                  attributes {
                    key
                    value
                  }
                  merchandise {
                    ... on ProductVariant {
                      id
                      title
                      image {
                        src: url
                        altText
                      }
                      product {
                        id
                        title
                        description
                        handle
                      }
                      priceV2 {
                        amount
                        currencyCode
                      }
                    }
                  }
                  discountAllocations {
                    discountApplication {
                      value {
                        ... on PricingPercentageValue {
                          percentage
                        }
                        ... on MoneyV2 {
                          amount
                        }
                      }
                    }
                    allocatedAmount:discountedAmount {
                      amount
                      currencyCode
                    }
                  }
                }
              }
            }
            cost {
              subtotalAmount {
                amount
                currencyCode
              }
              totalTaxAmount {
                amount
                currencyCode
              }
              totalAmount {
                amount
                currencyCode
              }
            }
          }
          userErrors {
            field
            message
          }
        }
      }
    `;

    const variables = { cartId, discountCodes: [discountCode] };

    // Helper function for success => either callback or return the updated cart
    const handleSuccess = (updatedCart) => {
      if (typeof callback === 'function') {
        callback(updatedCart);
        return;
      }
      // Promise style
      return updatedCart;
    };

    const handleError = (err) => {
      if (typeof callback === 'function') {
        callback(null);
        return;
      }
      throw err;
    };

    try {
      const { cartDiscountCodesUpdate } = await shopifyGraphQLRequest(query, variables);

      if (cartDiscountCodesUpdate.userErrors && cartDiscountCodesUpdate.userErrors.length > 0) {
        console.error('applyDiscount userErrors:', cartDiscountCodesUpdate.userErrors);
        return handleError(new Error('Failed to apply discount codes'));
      }
      this._removeDraftOrderInfoFromLocalStorage();
      const updatedCart = cartDiscountCodesUpdate.cart;
      // 5) Return the updated cart
      const flatCart = this._flattenCost(updatedCart);
      flatCart.lineItems = this._flattenCartLines(flatCart);
      // flatCart.discountApplications = flatCart.discountAllocations && flatCart.discountAllocations[0] ? flatCart.discountAllocations[0]['discountApplication'] : [];
      flatCart.discountApplications = this._fillDiscountApplications(flatCart);
      return handleSuccess(flatCart);
    } catch (error) {
      console.error('Error applying discount codes:', error);
      return handleError(null);
    }
  }

  getCollVariant(collectionId, isVip, isFromWebapp) {
    return this.getCollVariantNew(collectionId, isVip, isFromWebapp);
  }

  getProduct(productId) {
    const productGid = `gid://shopify/Product/${productId}`;
    return this.fetchProductById(productGid);
  }

  async getCollectionALaCarteVariantArray(collectionId, isVip, isFromWebapp) {
    try {
      // 1) Convert numeric ID to a GID if needed
      const collectionGid = this.normalizeCollectionId(collectionId);
  
      // 2) Fetch the collection via GraphQL
      const collection = await this.fetchCollectionById(collectionGid);
      if (!collection) {
        throw new Error(`No collection found for ID ${collectionId}`);
      }
  
      // 3) Build an array of variants
      const variantArr = [];
      const { products } = collection;
  
      for (const product of products) {
        // 4) Attempt to find a "VIP" variant or fallback
        let variant = this.getProductVariant(product, isVip);
        if (!variant && product.variantsArray?.length) {
          variant = product.variantsArray[0];
        }
  
        // 5) If still no variant, skip or handle
        if (!variant) {
          continue;
        }
  
        // 6) poolPrefix logic (similar to old approach)
        const poolPrefix = this.getVariantSelectedOption(variant, this.PRODUCT_OPTION_POOL_PREFIX);
        const unlockMedia = this.getVariantSelectedOption(variant, this.PRODUCT_OPTION_UNLOCK_MEDIA);
        // 7) Attach any extra fields
        variant.productTitle = product.title;
        variant.poolPrefix = poolPrefix;
        variant.unlockMedia = unlockMedia;
        variant.desc = product.description;
        if (isFromWebapp)
          variant.shipmentPoolPrefix = this.getShipmentPoolPrefixFromDescription(product.description);

        variantArr.push(variant);
      }
  
      return variantArr;
    } catch (err) {
      throw err;
    }
  }

  getShipmentPoolPrefixFromDescription(description) {
    let shipPoolPrefix = null;
    if (!description) {
      return null;
    }
    try {
      const descr = JSON.parse(description);
      if (descr && descr.shipment_pool_prefix) {
        shipPoolPrefix = descr.shipment_pool_prefix;
      }
    } catch (err) {
      console.log(err);
      return null;
    }
    return shipPoolPrefix;
  }

  getProductVariant(product, isVip) {
    return this._getProductVariant(product, isVip);
  }

  getVariantSelectedOption(prodVariant, optionName) {
    const { selectedOptions } = prodVariant || {};
    if (!Array.isArray(selectedOptions) || !selectedOptions.length) {
      return null;
    }
    const matchedOption = selectedOptions.find((opt) => opt.name === optionName);
    return matchedOption ? matchedOption.value : null;
  }

  normalizeProductId(id) {
    return `gid://shopify/Product/${id}`;
  }

  normalizeCollectionId(id) {
    return `gid://shopify/Collection/${id}`;
  }

  normalizeProductVariantId(id) {
    return `gid://shopify/ProductVariant/${id}`;
  }

  async getOrCreateCart() {
    const cartId = this._getStoredCartId();
    if (cartId) {
      let cart = await this.fetchCart(cartId);
      if (!cart) {
        return this.createAndStoreCart([]);
      }
      const shippingAddress = this._getShippingAddress();
      // If a shipping address is provided, update the cart with it
      if (this.isFromWebapp && shippingAddress && shippingAddress.address1) {
        try {
          cart = await this.updateCartAddress(cart.id, shippingAddress);
        } catch (error) {
          console.error('Failed to update cart address:', error);
          throw new Error('Cart created but failed to set shipping address');
        }
      }
      const flatCart = this._flattenCost(cart);
      flatCart.lineItems = this._flattenCartLines(flatCart);
      flatCart.discountApplications = this._fillDiscountApplications(flatCart);
      return flatCart;
    }
    const newCart = await this.createAndStoreCart([]);
    const flatCart = this._flattenCost(newCart);
    flatCart.lineItems = this._flattenCartLines(flatCart);
    return flatCart;
  }

  async cartLinesAdd(cartId, lines) {
    const query = `
      mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
        cartLinesAdd(cartId: $cartId, lines: $lines) {
          cart {
            id
            webUrl: checkoutUrl
            discountCodes {
              code
              applicable
            }
            discountAllocations {
              discountApplication {
                value {
                  ... on PricingPercentageValue {
                    percentage
                  }
                  ... on MoneyV2 {
                    amount
                  }
                }
              }
              allocatedAmount:discountedAmount {
                amount
                currencyCode
              }
            }
            lines(first: 50) {
              edges {
                node {
                  id
                  quantity
                  attributes {
                    key
                    value
                  }
                  merchandise {
                    ... on ProductVariant {
                      id
                      title
                      image {
                        src: url
                        altText
                      }
                      product {
                        id
                        title
                        description
                        handle
                      }
                      priceV2 {
                        amount
                        currencyCode
                      }
                    }
                  }
                  discountAllocations {
                    discountApplication {
                      value {
                        ... on PricingPercentageValue {
                          percentage
                        }
                        ... on MoneyV2 {
                          amount
                        }
                      }
                    }
                    allocatedAmount:discountedAmount {
                      amount
                      currencyCode
                    }
                  }
                }
              }
            }
            cost {
              subtotalPrice: subtotalAmount {
                amount
                currencyCode
              }
              totalTax: totalTaxAmount {
                amount
                currencyCode
              }
              totalPrice: totalAmount {
                amount
                currencyCode
              }
            }
            discountAllocations {
              discountApplication {
                value {
                  ... on PricingPercentageValue {
                    percentage
                  }
                  ... on MoneyV2 {
                    amount
                  }
                }
              }
              allocatedAmount:discountedAmount {
                amount
                currencyCode
              }
            }
            discountCodes {
              code
              applicable
            }
          }
          userErrors {
            field
            message
          }
        }
      }
    `;
    const variables = { cartId, lines };
    const { cartLinesAdd } = await shopifyGraphQLRequest(query, variables);

    if (cartLinesAdd.userErrors && cartLinesAdd.userErrors.length) {
      console.error('cartLinesAdd userErrors', cartLinesAdd.userErrors);
      throw new Error('Failed to add line items');
    }
    this._removeDraftOrderInfoFromLocalStorage();
    const flatCart = this._flattenCost(cartLinesAdd.cart);
    flatCart.lineItems = this._flattenCartLines(flatCart);
    flatCart.discountApplications = this._fillDiscountApplications(flatCart);
    return flatCart;
  }

  async cartLinesRemove(cartId, lineIds) {
    const query = `
      mutation cartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
        cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
          cart {
            id
            webUrl: checkoutUrl
            lines(first: 50) {
              edges {
                node {
                  id
                  quantity
                }
              }
            }
          }
          userErrors {
            field
            message
          }
        }
      }
    `;
    const variables = { cartId, lineIds };
    const { cartLinesRemove } = await shopifyGraphQLRequest(query, variables);

    if (cartLinesRemove.userErrors && cartLinesRemove.userErrors.length) {
      console.error('cartLinesRemove userErrors', cartLinesRemove.userErrors);
      throw new Error('Failed to remove line items');
    }
    this._removeDraftOrderInfoFromLocalStorage();
    return cartLinesRemove.cart;
  }

  async fetchCart(cartId) {
    const query = `
      query getCart($cartId: ID!) {
        cart(id: $cartId) {
          id
          webUrl: checkoutUrl
          lines(first: 50) {
            edges {
              node {
                id
                quantity
                attributes {
                  key
                  value
                }
                merchandise {
                  ... on ProductVariant {
                    id
                    title
                    image {
                      src: url
                      altText
                    }
                    product {
                      id
                      title
                      description
                      handle
                    }
                    priceV2 {
                      amount
                      currencyCode
                    }
                  }
                }
                discountAllocations {
                  discountApplication {
                    value {
                      ... on PricingPercentageValue {
                        percentage
                      }
                      ... on MoneyV2 {
                        amount
                      }
                    }
                  }
                  allocatedAmount: discountedAmount {
                    amount
                    currencyCode
                  }
                }
              }
            }
          }
          cost {
            subtotalPrice: subtotalAmount {
              amount
              currencyCode
            }
            totalTax: totalTaxAmount {
              amount
              currencyCode
            }
            totalPrice: totalAmount {
              amount
              currencyCode
            }
          }
          discountAllocations {
            discountApplication {
              value {
                ... on PricingPercentageValue {
                  percentage
                }
                ... on MoneyV2 {
                  amount
                }
              }
            }
            allocatedAmount: discountedAmount {
              amount
              currencyCode
            }
          }
          discountCodes {
            code
            applicable
          }
        }
      }
    `;
    const data = await shopifyGraphQLRequest(query, { cartId });
    return data.cart || null;
  }

  async createAndStoreCart(lines = []) {
    const query = `
      mutation cartCreate($lines: [CartLineInput!]!) {
        cartCreate(input: { lines: $lines }) {
          cart {
            id
            webUrl: checkoutUrl
            lines(first: 50) {
              edges {
                node {
                  id
                  quantity
                  attributes {
                    key
                    value
                  }
                  merchandise {
                    ... on ProductVariant {
                      id
                      title
                      image {
                        src: url
                        altText
                      }
                      product {
                        id
                        title
                        description
                        handle
                      }
                      priceV2 {
                        amount
                        currencyCode
                      }
                    }
                  }
                  discountAllocations {
                    discountApplication {
                      value {
                        ... on PricingPercentageValue {
                          percentage
                        }
                        ... on MoneyV2 {
                          amount
                        }
                      }
                    }
                    allocatedAmount: discountedAmount {
                      amount
                      currencyCode
                    }
                  }
                }
              }
            }
            cost {
              subtotalPrice: subtotalAmount {
                amount
                currencyCode
              }
              totalTax: totalTaxAmount {
                amount
                currencyCode
              }
              totalPrice: totalAmount {
                amount
                currencyCode
              }
            }
            discountAllocations {
              discountApplication {
                value {
                  ... on PricingPercentageValue {
                    percentage
                  }
                  ... on MoneyV2 {
                    amount
                  }
                }
              }
              allocatedAmount: discountedAmount {
                amount
                currencyCode
              }
            }
            discountCodes {
              code
              applicable
            }
          }
          userErrors {
            field
            message
          }
        }
      }
    `;
    const variables = { lines };
    const { cartCreate } = await shopifyGraphQLRequest(query, variables);

    if (cartCreate.userErrors && cartCreate.userErrors.length) {
      console.error('cartCreate userErrors', cartCreate.userErrors);
      throw new Error('Failed to create cart');
    }
    const newCart = cartCreate.cart;
    if (newCart) {
      this._setStoredCartId(newCart.id);
    }
    const shippingAddress = this._getShippingAddress();
    // If a shipping address is provided, update the cart with it
    if (this.isFromWebapp && shippingAddress && shippingAddress.address1) {
      try {
        const updatedCart = await this.updateCartAddress(newCart.id, shippingAddress);
        return updatedCart; // Return the updated cart with the shipping address
      } catch (error) {
        console.error('Failed to update cart address:', error);
        throw new Error('Cart created but failed to set shipping address');
      }
    }
    this._removeDraftOrderInfoFromLocalStorage();
    return newCart;
  }

  async updateCartAddress(cartId, newShippingAddress) {
    const query = `
      mutation UpdateCartBuyerIdentity($cartId: ID!, $buyerIdentity: CartBuyerIdentityInput!) {
        cartBuyerIdentityUpdate(cartId: $cartId, buyerIdentity: $buyerIdentity) {
          cart {
            id
            webUrl: checkoutUrl
            cost {
              totalTaxAmount {
                amount
                currencyCode
              }
              totalAmount {
                amount
                currencyCode
              }
            }
            lines(first: 50) {
              edges {
                node {
                  id
                  quantity
                  attributes {
                    key
                    value
                  }
                  merchandise {
                    ... on ProductVariant {
                      id
                      title
                      image {
                        src: url
                        altText
                      }
                      product {
                        id
                        title
                        description
                        handle
                      }
                      priceV2 {
                        amount
                        currencyCode
                      }
                    }
                  }
                  discountAllocations {
                    discountApplication {
                      value {
                        ... on PricingPercentageValue {
                          percentage
                        }
                        ... on MoneyV2 {
                          amount
                        }
                      }
                    }
                    allocatedAmount: discountedAmount {
                      amount
                      currencyCode
                    }
                  }
                }
              }
            }
            cost {
              subtotalPrice: subtotalAmount {
                amount
                currencyCode
              }
              totalTax: totalTaxAmount {
                amount
                currencyCode
              }
              totalPrice: totalAmount {
                amount
                currencyCode
              }
            }
            discountAllocations {
              discountApplication {
                value {
                  ... on PricingPercentageValue {
                    percentage
                  }
                  ... on MoneyV2 {
                    amount
                  }
                }
              }
              allocatedAmount: discountedAmount {
                amount
                currencyCode
              }
            }
            discountCodes {
              code
              applicable
            }
          }
          userErrors {
            field
            message
          }
        }
      }
    `;

    const variables = {
      cartId,
      buyerIdentity: {
        deliveryAddressPreferences: [
          {
            deliveryAddress: {
              address1: newShippingAddress.address1 || '',
              address2: newShippingAddress.address2 || '',
              city: newShippingAddress.city || '',
              province: newShippingAddress.province || '',
              country: newShippingAddress.country || '',
              zip: newShippingAddress.zip || '',
            },
          },
        ],
      },
    };

    try {
      const response = await shopifyGraphQLRequest(query, variables);
      const { cartBuyerIdentityUpdate } = response;

      if (cartBuyerIdentityUpdate.userErrors && cartBuyerIdentityUpdate.userErrors.length) {
        console.error('cartBuyerIdentityUpdate userErrors', cartBuyerIdentityUpdate.userErrors);
        throw new Error('Failed to update cart address');
      }

      return cartBuyerIdentityUpdate.cart;
    } catch (error) {
      console.error('Error updating cart address:', error);
      throw error;
    }
  }

  async fetchCollectionById(collectionGid) {
    const query = `
      query getCollection($id: ID!) {
        collection(id: $id) {
          id
          title
          description
          image {
            src: url
          }
          products(first: 50) {
            edges {
              node {
                id
                title
                description
                variants(first: 10) {
                  edges {
                    node {
                      id
                      title
                      priceV2 {
                        amount
                        currencyCode
                      }
                      availableForSale
                      selectedOptions {
                        name
                        value
                      }
                      image {
                        src: url
                        altText
                      }
                    }
                  }
                }
                options {
                  name
                  values
                }
              }
            }
          }
        }
      }
    `;
    const variables = { id: collectionGid };
    const { collection } = await shopifyGraphQLRequest(query, variables);
    if (!collection) return null;

    const productsArray = this._flattenEdges(collection.products);
    // flatten each product’s variants
    for (const product of productsArray) {
      product.variantsArray = this._flattenEdges(product.variants);
    }
    return { ...collection, products: productsArray };
  }

  async fetchProductById(productGid) {
    const query = `
      query getProduct($id: ID!) {
        product(id: $id) {
          id
          title
          variants(first: 10) {
            edges {
              node {
                id
                title
                availableForSale
                selectedOptions {
                  name
                  value
                }
                image {
                  src: url
                  altText
                }
              }
            }
          }
        }
      }
    `;
    const variables = { id: productGid };
    const response = await shopifyGraphQLRequest(query, variables);
    return response.product;
  }

  async getCollVariantNew(collectionId, isVip, isFromWebapp) {
    try {
      const collectionGid = this.normalizeCollectionId(collectionId);

      const collection = await this.fetchCollectionById(collectionGid);
      if (!collection) {
        throw new Error(`No collection found for ID ${collectionId}`);
      }

      const description = collection.description || '{}';
      const collObj = JSON.parse(description);
      const prodCount = collObj.product_list;
      const packProductId = collObj.pack_product;

      const packProductGid = this.normalizeProductId(packProductId);
      const packProd = await this.fetchProductById(packProductGid);
      if (!packProd) {
        throw new Error(`No product found for packProductId ${packProductId}`);
      }

      const variantProdPack = this.getProductVariant(packProd, isVip);

      const newProdCount = {};
      for (const pId of Object.keys(prodCount)) {
        const normalizedId = this.normalizeProductId(pId);
        newProdCount[normalizedId] = prodCount[pId];
      }

      const variantArr = [];
      const { products } = collection;
      let prod_group = 0;

      for (const product of products) {
        prod_group++;

        const resortFeeData = this._getResortDescriptionData(product);

        let variant = this.getProductVariant(product, isVip);

        if (!variant && product.variantsArray?.length) {
          variant = product.variantsArray[0];
        }

        const imageSrc = variant?.image?.src || '';

        const poolPrefix = this.getVariantSelectedOption(variant, this.PRODUCT_OPTION_POOL_PREFIX) || '';
        const unlockMedia = this.getVariantSelectedOption(variant, this.PRODUCT_OPTION_UNLOCK_MEDIA) || '';
        const shipmentPoolPrefix = this.getShipmentPoolPrefixFromDescription(product.description) || '';

        if (newProdCount[product.id]) {
          for (let i = 0; i < newProdCount[product.id]; i++) {
            const newVariant = {};
            newVariant.productTitle = product.title;
            newVariant.id = variant?.id;
            newVariant.pid = `${prod_group}_${i}`;
            newVariant.imgUrl = imageSrc;
            newVariant.poolPrefix = poolPrefix;
            newVariant.unlockMedia = unlockMedia;
            newVariant.desc = product.description;
            if (isFromWebapp)
              newVariant.shipmentPoolPrefix = shipmentPoolPrefix;

            variantArr.push(newVariant);
          }
        } else {
          const newVariant = {};
          if (resortFeeData) {
            const packVariantEdge = packProd.variants?.edges?.[isVip];
            const packVariantNode = packVariantEdge?.node;
            if (packVariantNode?.priceV2) {
              let fee = (packVariantNode.priceV2.amount * resortFeeData.fee_persent) / 100;
              fee = Math.round(fee * 100) / 100;
              const ttl = `${product.title} (${resortFeeData.fee_persent}% included) - ${this.formatPrice({
                amount: fee,
                currencyCode: packVariantNode.priceV2.currencyCode,
              })}`;
              newVariant.productTitle = ttl;
              newVariant.isResortFee = true;
            } else {
              newVariant.productTitle = product.title;
            }
          } else {
            newVariant.productTitle = product.title;
          }
          newVariant.id = variant?.id;
          newVariant.pid = `${prod_group}_0`;
          newVariant.imgUrl = imageSrc;
          newVariant.poolPrefix = poolPrefix;
          newVariant.unlockMedia = unlockMedia;
          newVariant.desc = product.description;

          variantArr.push(newVariant);
        }
      }

      variantProdPack.variantArr = variantArr;
      return variantProdPack;
    } catch (err) {
      throw err;
    }
  }

  _getProductVariant(product, isVip) {
    const prodVariantList = product?.variants?.edges ? product.variants.edges.map((edge) => edge.node) : [];
    if (prodVariantList.length < 1) return null;
    for (const prodVariant of prodVariantList) {
      const variantIsVipValue = this.getVariantSelectedOption(prodVariant, this.PRODUCT_OPTION_IS_VIP);
      if (!isVip || isVip === variantIsVipValue || prodVariantList.length === 1) {
        return prodVariant;
      }
    }
    return null;
  }

  async resetCart() {
    this._removeDraftOrderInfoFromLocalStorage();
    this._removeStoredCartId();
    return this.createAndStoreCart([]);
  }

  // -------------------------------------------------------------------------
  // Private utilities
  // -------------------------------------------------------------------------
  _flattenEdges(connection) {
    if (!connection || !connection.edges) return [];
    return connection.edges.map((edge) => edge.node);
  }

  _getStoredCartId() {
    return localStorage.getItem(this.localStorageCheckoutIdKey);
  }

  _setStoredCartId(cartId) {
    localStorage.setItem(this.localStorageCheckoutIdKey, cartId);
  }

  _removeStoredCartId() {
    localStorage.removeItem(this.localStorageCheckoutIdKey);
  }

  _getResortDescriptionData(product) {
    if (!product.description) {
      return null;
    }
    const resortDescriptionData = {};
    try {
      // { "key_type": "resort", "fee_persent": 6 }
      const descr = JSON.parse(product.description);
      if (descr && descr.product_type === 'resort_fee') {
        resortDescriptionData.isResortFee = true;
        if (descr.fee_persent) resortDescriptionData.fee_persent = descr.fee_persent;
        else return null;
      } else {
        return null;
      }
    } catch (err) {
      console.log(err);
      return null;
    }
    return resortDescriptionData;
  }

  _flattenCartLines(cart) {
    if (!cart || !cart.lines || !cart.lines.edges) {
      return [];
    }

    return cart.lines.edges.map((edge) => {
      const { node } = edge;
      const merch = node.merchandise || {};
      return {
        id: node.id,
        quantity: node.quantity,

        merchandiseId: merch.id,
        title: merch.product.title,
        variant: merch,
        discountAllocations: node.discountAllocations,
        customAttributes: node.attributes,
      };
    });
  }

  _flattenCost(cart) {
    if (!cart?.cost) return cart;
    const { cost, ...rest } = cart;
    return {
      ...rest,
      subtotalPrice: cost.subtotalPrice,
      totalPrice: cost.totalPrice,
      totalTax: cost.totalTax,
    };
  }

  _calculateDiscountApplicationValueAmount(cart) {
    const values = cart.discountAllocations
      .filter((allocation) => allocation.discountApplication)
      .map((allocation) => allocation.discountApplication.value);
    const totalDiscount = values.every((value) => value.amount)
      ? values.reduce((sum, value) => sum + parseFloat(value.amount), 0)
      : null;
    return totalDiscount;
  }

  _fillDiscountApplications(flatCart) {
    const discountApplications = [];
    if (flatCart.discountAllocations && flatCart.discountAllocations[0]) {
      discountApplications[0] = {
        ...flatCart.discountAllocations[0].discountApplication,
        title: flatCart.discountCodes[0].code,
        allocatedAmount: flatCart.discountAllocations[0].discountedAmount,
      };
      const discountApplicationValueAmount = this._calculateDiscountApplicationValueAmount(flatCart);
      if (discountApplicationValueAmount) {
        discountApplications[0].value = { amount: discountApplicationValueAmount };
      }
    }
    return discountApplications;
  }

  _removeDraftOrderInfoFromLocalStorage() {
    localStorage.removeItem('draftOrderInfo');
  }

  _getShippingAddress() {
    const shippingAddress = this.SHIPPING_ADDRESS;
    if (
      !shippingAddress ||
      shippingAddress === 'null' ||
      shippingAddress === 'undefined' ||
      shippingAddress.length < 50
    )
      return;

    const shippAddresObj = JSON.parse(shippingAddress);
    return {
      address1: shippAddresObj.shopifyAddress1,
      address2: shippAddresObj.shopifyAddress2,
      city: shippAddresObj.shopifyCity,
      company: shippAddresObj.shopifyCompany,
      country: shippAddresObj.shopifyCountry,
      firstName: shippAddresObj.shopifyFirstName,
      lastName: shippAddresObj.shopifyLastName,
      phone: shippAddresObj.shopifyPhoneNumber,
      province: shippAddresObj.shopifyState,
      zip: shippAddresObj.shopifyZip,
    };
  }
}
